// Code-action: "Add missing table to FROM" — OE-tree walks.
//
// Split out of addMissingTableToFrom.ts (behavior-neutral).

import type { DatabaseExplorerNode } from '../../../explorer/models/DatabaseTreeModels';
import type { TableMatch } from './addMissingTableToFrom.types';

export function findTableByName(
  treeData: readonly DatabaseExplorerNode[],
  connectionId: string,
  database: string | null,
  targetName: string,
  defaultSchema: string,
  preferredSchema?: string,
): TableMatch | null {
  let result: TableMatch | null = null;
  walk(treeData, undefined);
  return result;

  function walk(nodes: readonly DatabaseExplorerNode[] | undefined, currentDb: string | undefined): void {
    if (!nodes || result) return;
    for (const node of nodes) {
      if (result) return;
      if (node.type === 'connection') {
        const meta = node.metadata as { id?: string } | undefined;
        if (meta?.id !== connectionId) continue;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, undefined);
        continue;
      }
      if (node.type === 'database') {
        const meta = node.metadata as { name?: string } | undefined;
        if (database && (meta?.name ?? '').toLowerCase() !== database.toLowerCase()) continue;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, meta?.name);
        continue;
      }
      if (node.type === 'table') {
        const meta = node.metadata as { objectDetails?: { name?: string; schema?: string } } | undefined;
        const od = meta?.objectDetails;
        if (od?.name && od.name.toLowerCase() === targetName.toLowerCase()) {
          const sch = (od.schema ?? defaultSchema).toLowerCase();
          if (preferredSchema && sch !== preferredSchema.toLowerCase() && sch !== defaultSchema.toLowerCase()) {
            walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
            continue;
          }
          result = { node, name: od.name, schema: sch };
          return;
        }
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
        continue;
      }
      walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
    }
  }
}

export function findTablesWithColumn(
  treeData: readonly DatabaseExplorerNode[],
  connectionId: string,
  database: string | null,
  columnFolded: string,
  defaultSchema: string,
): readonly TableMatch[] {
  const out: TableMatch[] = [];
  walk(treeData, undefined);
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;

  function walk(nodes: readonly DatabaseExplorerNode[] | undefined, currentDb: string | undefined): void {
    if (!nodes) return;
    for (const node of nodes) {
      if (node.type === 'connection') {
        const meta = node.metadata as { id?: string } | undefined;
        if (meta?.id !== connectionId) continue;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, undefined);
        continue;
      }
      if (node.type === 'database') {
        const meta = node.metadata as { name?: string } | undefined;
        if (database && (meta?.name ?? '').toLowerCase() !== database.toLowerCase()) continue;
        walk(node.children as readonly DatabaseExplorerNode[] | undefined, meta?.name);
        continue;
      }
      if (node.type === 'table') {
        const meta = node.metadata as { objectDetails?: { name?: string; schema?: string } } | undefined;
        const od = meta?.objectDetails;
        if (od?.name && tableOwnsColumn(node, columnFolded)) {
          out.push({
            node,
            name: od.name,
            schema: (od.schema ?? defaultSchema).toLowerCase(),
          });
        }
        continue;
      }
      walk(node.children as readonly DatabaseExplorerNode[] | undefined, currentDb);
    }
  }
}

export function tableOwnsColumn(tableNode: DatabaseExplorerNode, columnFolded: string): boolean {
  const cols = collectColumnNames(tableNode.children as readonly DatabaseExplorerNode[] | undefined);
  return cols.some((c) => c.toLowerCase() === columnFolded);
}

function collectColumnNames(children: readonly DatabaseExplorerNode[] | undefined): readonly string[] {
  if (!children) return [];
  const out: string[] = [];
  for (const child of children) {
    if (child.type === 'columns-folder') {
      for (const col of child.children ?? []) {
        if (col.type === 'column') {
          const meta = col.metadata as { columnName?: string } | undefined;
          if (meta?.columnName) out.push(meta.columnName);
        }
      }
      continue;
    }
    if (child.type === 'column') {
      const meta = child.metadata as { columnName?: string } | undefined;
      if (meta?.columnName) out.push(meta.columnName);
    }
  }
  return out;
}

export function relationOwnsColumn(
  treeData: readonly DatabaseExplorerNode[],
  connectionId: string,
  database: string | null,
  name: string,
  schema: string,
  columnFolded: string,
): boolean {
  const match = findTableByName(treeData, connectionId, database, name, schema, schema);
  if (!match) return false;
  return tableOwnsColumn(match.node, columnFolded);
}
